import React, { useState, useMemo, useEffect, useContext } from "react";
import { Form, Switch } from "antd";
import InputMask from "react-input-mask";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import BackArrow from "../../../../newUXAssets/icons/BackArrow";
import Input from "../../../../newUXComponents/Input";
import Select from "../../../../newUXComponents/Select";
import Button from "../../../../newUXComponents/Button";
import TelephoneInput, {
  getFullTelephoneNumber,
} from "../../../../newUXComponents/TelephoneInput";
import useNotification from "../../../../hooks/newUXNotification";
import { DialogContext } from "../../../../hooks/dialog/context";
import { useLoading } from "../../../../hooks/loading";
import {
  postNewEmployee,
  getNewEmployeeCategories,
} from "../../../../redux/modules/employee/actions";
import { getCountries } from "react-phone-number-input/input";
import countryListEn from "react-phone-number-input/locale/en";
import countryListPt from "react-phone-number-input/locale/pt";
import { validation, INITIAL_VALUES } from "./validations";
import ConfirmationModal from "./ConfirmationModal";
import {
  Container,
  ReturnLink,
  Title,
  ButtonContainer,
  SwitchContainer,
  FormContainer,
  CustomExtraLabel,
} from "./styles";
import {
  getBrazilianStates,
  getBrazilianCitiesByStateId,
} from "../../../../service/localities.service";
import {
  buildCredentialName,
  allowCredentialNameToReceiveValue,
} from "../../../../utils/credentialName";

const NewEmployee = () => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const notification = useNotification();
  const { handleLoading } = useLoading();
  const { createDialog } = useContext(DialogContext);

  const [isCreateCredential, setIsCreateCredential] = useState(false);
  const [documentMask, setDocumentMask] = useState("999.999.999-99");
  const [documentLabel, setDocumentLabel] = useState(
    intl.formatMessage({ id: "document_cpf" })
  );
  const [nationality, setNationality] = useState(false);
  const [documentHasError, setDocumentHasError] = useState(false);

  const { event, contract } = useSelector((state) => state.initialSettings);
  const { categories } = useSelector((state) => state.employee);
  const { permission } = useSelector((state) => state.auth);

  const [countries, setCountries] = useState([]);
  const [brazilianStates, setBrazilianStates] = useState([]);
  const [brazilianCities, setBrazilianCities] = useState([]);
  const [canApplyCredentialName, setCanApplyCredentialName] = useState(true);
  const isPortuguese = useMemo(() => intl.locale === "pt", [intl]);

  const havePermission = useMemo(() => {
    return permission.includes("MANAGE_CREDENTIALS");
  }, [permission]);

  const handleTypeChanges = (value) => {
    setNationality(value);
    setDocumentHasError(false);
    switch (value) {
      case false:
        setDocumentLabel(intl.formatMessage({ id: "document_cpf" }));
        setDocumentMask("999.999.999-99");
        break;
      case true:
        setDocumentLabel(intl.formatMessage({ id: "document_passport" }));
        setDocumentMask("");
        break;
      default:
        setDocumentLabel(intl.formatMessage({ id: "document" }));
        break;
    }
    form.setFieldValue("document", "");
    form.setFieldValue("country", undefined);
    form.setFieldValue("internationalCity", "");
    form.setFieldValue("state", undefined);
    form.setFieldValue("brazilianCity", undefined);
    setBrazilianCities([]);
    form.validateFields(["document"]);
  };

  const handleDocumentChange = (e) => {
    const value = e.target.value;

    if (nationality === true) {
      const upperValue = value.toUpperCase();
      form.setFieldValue("document", upperValue);
    } else {
      form.setFieldValue("document", value);
    }

    setTimeout(() => {
      form
        .validateFields(["document"])
        .then(() => {
          setDocumentHasError(false);
        })
        .catch((error) => {
          setDocumentHasError(true);
        });
    }, 300);
  };

  const handleDocumentBlur = () => {
    const value = form.getFieldValue("document");

    if (!value) return;

    form
      .validateFields(["document"])
      .then(() => {
        setDocumentHasError(false);
      })
      .catch((error) => {
        setDocumentHasError(true);
      });
  };

  const callBackError = (message) => {
    handleLoading(false);
    notification.displayError(
      intl.formatMessage({ id: "api.error." + message }),
      "post-new-employee-error-message"
    );
  };

  const callBackSuccess = (message, key) => {
    handleLoading(false);
    notification.displaySuccess(
      intl.formatMessage({ id: "success." + message }),
      "post-new-employee-success-message"
    );
    setIsCreateCredential(false);
    createDialog({
      id: `new-employee-confirmation-display-${key}`,
      Component: ConfirmationModal,
      props: {
        onConfirmation: handleConfirmation,
        handleClose: handleGoBack,
      },
    });
  };

  const handleGetNewEmployeeCategories = (value) => {
    setIsCreateCredential(value);
    if (value) {
      dispatch(
        getNewEmployeeCategories({
          selectedEvent: event.value,
          selectedContract: contract.value,
          callbackError: callBackError,
          isPortuguese,
        })
      );
    }
  };

  const handleBuildCredentialName = () => {
    if (!canApplyCredentialName) return;

    const { name, last_name } = form.getFieldsValue(["name", "last_name"]);
    const credentialName = buildCredentialName(name, last_name);

    if (!credentialName.trim()) return;

    form.setFieldValue("credential_name", credentialName);
    form.validateFields(["credential_name"]);
  };

  const handleCredentialNameChange = () => {
    const {
      name,
      last_name,
      credential_name
    } = form.getFieldsValue(["name", "last_name", "credential_name"]);

    setCanApplyCredentialName(
      allowCredentialNameToReceiveValue(name, last_name, credential_name));
  };

  const fetchBrazilianCities = async (stateId) => {
    try {
      handleLoading(true);
      const cities = await getBrazilianCitiesByStateId(stateId);
      setBrazilianCities(cities);
    } catch (error) {
      notification.displayError(
        intl.formatMessage({ id: "error.loading_cities" }),
        "error-new-employee-loading-cities"
      );
      setBrazilianCities([]);
    } finally {
      handleLoading(false);
    }
  };

  const handleStateChange = (stateValue, option) => {
    form.setFieldValue("brazilianCity", undefined);

    if (!option?.stateId) {
      setBrazilianCities([]);
      return;
    }

    fetchBrazilianCities(option.stateId);
  };

  useEffect(() => {
    const fetchBrazilianStates = async () => {
      try {
        handleLoading(true);
        const states = await getBrazilianStates();
        setBrazilianStates(states);
      } catch (error) {
        notification.displayError(
          intl.formatMessage({ id: "error.loading_states" }),
          "error-new-employee-loading-states"
        );
        setBrazilianStates([]);
      } finally {
        handleLoading(false);
      }
    };

    const allCountries = getCountries();
    const countryList = isPortuguese ? countryListPt : countryListEn;
    const countriesOptions = allCountries.map((countryCode) => ({
      label: countryList[countryCode],
      value: countryList[countryCode],
    }));
    const sortedCountries = countriesOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    setCountries(sortedCountries);

    fetchBrazilianStates();
  }, [isPortuguese]);

  const onFinish = (values) => {
    if (!event || !contract) return;

    handleLoading(true);

    const newEmployee = {
      international: values.nationality,
      name: values.name,
      lastName: values.last_name,
      credentialName: values.credential_name,
      document: values.document,
      jobTitle: values.assignment,
      email: values.email,
      country: values.country || null,
      city: values.nationality
        ? values.internationalCity
        : values.brazilianCity,
      state: values.state,
      telephone: getFullTelephoneNumber(
        values.telephone,
        values.telephone_selected_country.value
      ),
      createCredential: isCreateCredential,
      eventId: event.value,
      opportunityId: contract.value,
      credentialType: values.credentialType,
    };
    dispatch(
      postNewEmployee({ employee: newEmployee, callBackError, callBackSuccess })
    );
  };

  const handleGoBack = () => {
    history.push("/credentials/register");
  };

  const handleConfirmation = () => {
    form.resetFields();
    form.setFieldValue("nationality", false);
    form.setFieldValue("create_credential", false);
    form.setFieldValue("telephone_selected_country", {
      value: "BR",
    });
    setBrazilianCities([]);
    setNationality(false);
    setDocumentHasError(false);
    setDocumentLabel(intl.formatMessage({ id: "document_cpf" }));
    setDocumentMask("999.999.999-99");
  };

  const labelWithExtra = (labelKey, extraKey, extraValues = {}) => (
    <span>
      {intl.formatMessage({ id: labelKey })}
      <CustomExtraLabel>
        {intl.formatMessage({ id: extraKey }, extraValues)}
      </CustomExtraLabel>
    </span>
  );

  useEffect(() => {
    if (!event?.value && !contract?.value) {
      notification.displayInfo(
        intl.formatMessage({ id: "error.contract_not_selected" }),
        "event-not-selected-message"
      );
    } else {
      notification.close("event-not-selected-message");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.value, contract?.value]);

  return (
    <Container>
      <ReturnLink onClick={handleGoBack}>
        <BackArrow />
        <p>{intl.formatMessage({ id: "goback" })}</p>
      </ReturnLink>
      <Title>{intl.formatMessage({ id: "new_collaborator" })}</Title>
      {contract?.value && (
        <FormContainer>
          <Form
            layout="vertical"
            form={form}
            name="new-employee"
            onFinish={onFinish}
            initialValues={INITIAL_VALUES}
          >
            <Select
              name="nationality"
              label={intl.formatMessage({ id: "nationality" })}
              showArrow
              allowClear
              placeholder={intl.formatMessage({ id: "nationality" })}
              rules={validation("nationality", intl)}
              onChange={(e) => handleTypeChanges(e)}
              options={[
                {
                  label: intl.formatMessage({ id: "national" }),
                  value: false,
                },
                {
                  label: intl.formatMessage({ id: "international" }),
                  value: true,
                },
              ]}
            />
            <Input
              type="text"
              name="name"
              label={intl.formatMessage({ id: "name" })}
              placeholder={intl.formatMessage({ id: "name" })}
              rules={validation("name", intl)}
              onBlur={handleBuildCredentialName}
            />
            <Input
              type="text"
              name="last_name"
              label={intl.formatMessage({ id: "last_name" })}
              placeholder={intl.formatMessage({ id: "last_name" })}
              rules={validation("last_name", intl)}
              onBlur={handleBuildCredentialName}
            />
            <Input
              type="text"
              name="credential_name"
              label={labelWithExtra(
                "credential_name",
                "up_to_custom_characters",
                { quantity: 23 }
              )}
              placeholder={intl.formatMessage({ id: "credential_name" })}
              rules={validation("credential_name", intl)}
              onBlur={handleCredentialNameChange}
            />
            <Input
              type="text"
              name="document"
              as={InputMask}
              mask={documentMask}
              placeholder={documentLabel}
              label={documentLabel}
              rules={validation("document", intl, nationality)}
              onChange={handleDocumentChange}
              onBlur={handleDocumentBlur}
              style={{
                borderColor: documentHasError ? "#ff4d4f" : undefined,
                boxShadow: documentHasError
                  ? "0 0 0 2px rgba(255, 77, 79, 0.2)"
                  : undefined,
              }}
            />
            <Input
              type="text"
              name="assignment"
              label={labelWithExtra(
                "assignment",
                "up_to_custom_characters",
                { quantity: 50 }
              )}
              placeholder={intl.formatMessage({ id: "assignment" })}
              rules={validation("assignment", intl)}
            />
            <Input
              type="text"
              name="email"
              label={intl.formatMessage({ id: "email" })}
              placeholder={intl.formatMessage({ id: "email" })}
              rules={validation("email", intl)}
            />
            {nationality ? (
              <>
                <Select
                  name="country"
                  label={intl.formatMessage({ id: "country" })}
                  allowClear
                  placeholder={intl.formatMessage({
                    id: "select_country_where_live"
                  })}
                  rules={validation("country", intl, nationality)}
                  options={countries}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
                <Input
                  type="text"
                  name="internationalCity"
                  label={intl.formatMessage({ id: "city" })}
                  placeholder={intl.formatMessage({
                    id: "type_city_where_live"
                  })}
                  rules={validation("internationalCity", intl, nationality)}
                  maxLength={255}
                />
              </>
            ) : (
              <>
                <Select
                  name="state"
                  label={intl.formatMessage({ id: "state" })}
                  placeholder={intl.formatMessage({
                    id: "select_state_where_live"
                  })}
                  rules={validation("state", intl, nationality)}
                  options={brazilianStates}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleStateChange}
                />
                <Select
                  name="brazilianCity"
                  label={intl.formatMessage({ id: "city" })}
                  placeholder={intl.formatMessage({
                    id: "select_city_where_live"
                  })}
                  rules={validation("brazilianCity", intl, nationality)}
                  options={brazilianCities}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </>
            )}
            <TelephoneInput
              name="telephone"
              placeholder={intl.formatMessage({
                id: "telephone",
              })}
              label={intl.formatMessage({
                id: "telephone",
              })}
              rules={validation("telephone", intl)}
              refForm={form}
            />
            {havePermission && (
              <>
                <SwitchContainer>
                  <Switch
                    onChange={(e) => handleGetNewEmployeeCategories(e)}
                    checked={isCreateCredential}
                  />
                  <p>
                    {intl.formatMessage({
                      id: "create_credential_collaborator_page",
                    })}
                  </p>
                </SwitchContainer>
                {isCreateCredential && (
                  <Select
                    name="credentialType"
                    label={intl.formatMessage({ id: "credential_types" })}
                    showArrow
                    allowClear
                    placeholder={intl.formatMessage({ id: "credential_types" })}
                    rules={validation("credentialType", intl)}
                    options={categories}
                  />
                )}
              </>
            )}

            <ButtonContainer>
              <Button
                label={intl.formatMessage({ id: "cancel" })}
                type="link"
                onClick={handleGoBack}
              />
              <Button
                label={intl.formatMessage({ id: "save" })}
                type="primary"
                htmlType="submit"
              />
            </ButtonContainer>
          </Form>
        </FormContainer>
      )}
    </Container>
  );
};

export default NewEmployee;
